import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import {
  DocumentResponseDto,
  DocumentListItemDto,
  DocumentsListParams,
  useDeleteDocumentMutation,
  useGetDocumentsListQuery,
} from "../../../api/documentsApi";
import { DEFAULT_DATA_TABLE_PAGE_SIZE } from "../../../constants/dataTable";
import { useDataTablePagination } from "../../../hooks/useDataTablePagination";
import { PaginatedQueryParams } from "../../../types/pagination";
import {
  Action,
  DataTable,
  Column,
} from "../../../components/common/DataTable/DataTable";
import { SearchableColumn } from "../../../components/common/SearchInputs";
import {
  DocumentEntityOption,
  DocumentEntityType,
} from "../../../components/documents/DocumentUploadModal";
import {
  createDocumentActions,
  createDocumentColumns,
  getDocumentRowSx,
} from "../../../components/documents/documentColumns";

interface DocumentsTableProps {
  entityType: DocumentEntityType;
  entityLabel: string;
  entityOptions?: DocumentEntityOption[];
  showEntityFilter?: boolean;
  fitContentHeight?: boolean;
}

const CATEGORY_OPTIONS = [
  { value: "Insurance", label: "Insurance" },
  { value: "Registration", label: "Registration" },
  { value: "Inspection", label: "DOT Inspection" },
  { value: "Maintenance", label: "Maintenance Records" },
  { value: "License", label: "License/Permits" },
  { value: "Manual", label: "Owner's Manual" },
  { value: "Warranty", label: "Warranty Information" },
  { value: "PoliceReport", label: "Police Report" },
  { value: "Other", label: "Other" },
];

const EXPIRATION_STATUS_OPTIONS = [
  { value: "expired", label: "Expired" },
  { value: "critical", label: "7 days or less" },
  { value: "warning", label: "8-30 days" },
  { value: "valid", label: "31+ days" },
  { value: "noExpiration", label: "No expiration" },
];

const PERMIT_OPTIONS = [
  { value: "true", label: "Permit" },
  { value: "false", label: "Not permit" },
];

const buildDocumentsListQueryArg = (
  entityType: DocumentEntityType,
  queryParams: PaginatedQueryParams
): DocumentsListParams => {
  const filters = queryParams.filters ?? {};
  const isPermitFilter = filters["isPermit"];

  return {
    pageNumber: queryParams.pageNumber,
    pageSize: queryParams.pageSize,
    entityType,
    entityId: filters["entityName"] ? Number(filters["entityName"]) : undefined,
    documentCategory: filters["documentCategory"] as string | undefined,
    search: filters["originalFileName"] as string | undefined,
    status: filters["expirationDate"] as string | undefined,
    isPermit:
      isPermitFilter === undefined || isPermitFilter === null
        ? undefined
        : isPermitFilter === "true",
  };
};

const DocumentsTable: React.FC<DocumentsTableProps> = ({
  entityType,
  entityLabel,
  entityOptions = [],
  showEntityFilter = true,
  fitContentHeight = false,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] =
    useState<DocumentResponseDto | null>(null);

  const buildQueryArg = useMemo(
    () => (queryParams: PaginatedQueryParams) =>
      buildDocumentsListQueryArg(entityType, queryParams),
    [entityType]
  );

  const {
    items: documents,
    isLoading,
    isLoadingRows,
    isLoadingMore,
    totalCount,
    hasNextPage,
    handleSearchChange,
    handleLoadMore,
    resetTableItems,
    error,
  } = useDataTablePagination<DocumentListItemDto, DocumentsListParams>(
    useGetDocumentsListQuery,
    DEFAULT_DATA_TABLE_PAGE_SIZE,
    { buildQueryArg }
  );

  const [deleteDocument, { isLoading: isDeleting }] =
    useDeleteDocumentMutation();

  const columns: Column[] = useMemo(
    () => [
      {
        field: "entityName",
        headerName: entityLabel,
        width: 180,
        renderCell: (value) => (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {value || "-"}
          </Typography>
        ),
      },
      ...createDocumentColumns(),
    ],
    [entityLabel]
  );

  const searchableColumns: SearchableColumn[] = [
    ...(showEntityFilter
      ? [
          {
            field: "entityName",
            headerName: entityLabel,
            searchable: true,
            searchType: "select" as const,
            selectOptions: entityOptions.map((option) => ({
              value: option.id,
              label: option.label,
            })),
          },
        ]
      : []),
    {
      field: "originalFileName",
      headerName: "Document",
      searchable: true,
      searchType: "string",
    },
    {
      field: "documentCategory",
      headerName: "Category",
      searchable: true,
      searchType: "select",
      selectOptions: CATEGORY_OPTIONS,
    },
    {
      field: "expirationDate",
      headerName: "Expiration",
      searchable: true,
      searchType: "select",
      selectOptions: EXPIRATION_STATUS_OPTIONS,
    },
    {
      field: "isPermit",
      headerName: "Permit",
      searchable: true,
      searchType: "select",
      selectOptions: PERMIT_OPTIONS,
    },
  ];

  const handleView = (document: DocumentResponseDto) => {
    window.open(document.url, "_blank");
  };

  const handleDeleteClick = (document: DocumentResponseDto) => {
    setDocumentToDelete(document);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;

    try {
      await deleteDocument(documentToDelete.id).unwrap();
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
      resetTableItems();
    } catch (error) {
      console.error("Failed to delete document:", error);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };

  const documentActions = createDocumentActions({
    isDeleting,
    onDelete: handleDeleteClick,
    onDownload: handleView,
    onView: handleView,
  }) as Action<DocumentListItemDto>[];

  return (
    <Box>
      <>
        {error && (
          <Box sx={{ mb: 2 }}>
            <Typography color="error">
            Error loading documents. Please try again later.
            </Typography>
          </Box>
        )}

        <DataTable<DocumentListItemDto>
          items={documents}
          columns={columns}
          actions={documentActions}
          isLoading={isLoading}
          totalCount={totalCount}
          hasNextPage={hasNextPage}
          isLoadingMore={isLoadingMore}
          isLoadingRows={isLoadingRows}
          onLoadMore={handleLoadMore}
          getRowSx={getDocumentRowSx}
          fitContentHeight={fitContentHeight}
          enableSearch
          searchableColumns={searchableColumns}
          onSearchChange={handleSearchChange}
        />

        <Dialog
          open={deleteDialogOpen}
          onClose={handleCancelDelete}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">Delete Document</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to delete "
              {documentToDelete?.originalFileName}"? This action cannot be
              undone and will permanently remove the document from the system.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    </Box>
  );
};

export default DocumentsTable;
